import { useState } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { Capacitor } from '@capacitor/core'
import { ChevronLeft, ChevronRight } from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { cn } from '@/lib/utils'
import { MonthPickerDrawer } from './MonthPickerDrawer'

interface RosterDateNavProps {
  /** The currently displayed month. */
  month: Dayjs
  /** Called with the new month (first day) when prev/next is tapped or a month is picked. */
  onMonthChange: (month: Dayjs) => void
  disablePrev?: boolean
  disableNext?: boolean
  className?: string
}

// Same split as DateInput: inside the Capacitor app the OS month/year wheel
// (`<input type="month">`) is the picker; browsers get the custom drawer
// (desktop Safari/Firefox render type="month" as a plain text field).
const isNativePlatform = Capacitor.isNativePlatform()

const TITLE_CLASS =
  'px-3 py-1 rounded-lg text-sm font-medium text-content-primary-bold tracking-[0.07px] active:bg-surface-L1 transition-colors'

/**
 * Shared month navigation (‹ Month YYYY ›) rendered above the roster tabs.
 * Tapping the title opens a month/year picker. Purely presentational: it
 * reports the requested month; the parent persists it (typically to the
 * `selectedMonth` URL param the views read from).
 */
export function RosterDateNav({
  month,
  onMonthChange,
  disablePrev,
  disableNext,
  className,
}: RosterDateNavProps) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const go = (next: Dayjs) => {
    hapticLight()
    onMonthChange(next)
  }

  const title = month.format('MMMM YYYY')

  return (
    <>
      <div className={cn('flex items-center justify-between', className)}>
        <button
          onClick={() => go(month.subtract(1, 'month').startOf('month'))}
          disabled={disablePrev}
          aria-label="Previous month"
          className="flex items-center justify-center size-8 rounded-lg border border-border-L2 bg-surface-brand-L0 shadow-sm active:scale-95 transition disabled:opacity-50"
        >
          <ChevronLeft className="size-4 text-content-primary-bold" />
        </button>

        {isNativePlatform ? (
          // The invisible input sits over the title so a tap on the text opens
          // the OS picker directly; the visible label stays our own typography.
          <label className={cn('relative', TITLE_CLASS)}>
            {title}
            <input
              type="month"
              value={month.format('YYYY-MM')}
              aria-label={`Change month, currently ${title}`}
              onClick={() => hapticLight()}
              onChange={(e) => {
                const v = e.target.value
                if (!v) return // Android "Clear" — keep the current month
                const next = dayjs(v, 'YYYY-MM')
                if (next.isValid()) onMonthChange(next.startOf('month'))
              }}
              className="absolute inset-0 size-full opacity-0"
            />
          </label>
        ) : (
          <button
            onClick={() => {
              hapticLight()
              setPickerOpen(true)
            }}
            aria-haspopup="dialog"
            aria-label={`Change month, currently ${title}`}
            className={TITLE_CLASS}
          >
            {title}
          </button>
        )}

        <button
          onClick={() => go(month.add(1, 'month').startOf('month'))}
          disabled={disableNext}
          aria-label="Next month"
          className="flex items-center justify-center size-8 rounded-lg border border-border-L2 bg-surface-brand-L0 shadow-sm active:scale-95 transition disabled:opacity-50"
        >
          <ChevronRight className="size-4 text-content-primary-bold" />
        </button>
      </div>

      {!isNativePlatform && (
        <MonthPickerDrawer
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          month={month}
          onSelect={onMonthChange}
        />
      )}
    </>
  )
}
